import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, Reorder, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Upload, 
  Play, 
  Pause, 
  X, 
  GripVertical, 
  Music,
  FileAudio,
  AlertCircle,
  FileText,
  Plus,
  TrendingUp,
  ChevronLeft,
  Download,
  FolderOpen,
  Settings,
  Folder
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { LyricsManager } from '@/components/lyrics/LyricsManager';
import { hasLyrics, getSongFilename, LYRICS_CHANGED_EVENT } from '@/utils/lyricsUtils';
import { PlaylistHeader } from '@/components/playlist/PlaylistHeader.jsx';
import { PlaylistStats } from '@/components/playlist/PlaylistStats.jsx';
import { PlaylistItem } from '@/components/playlist/PlaylistItem.jsx';
import { useAudioContext } from '@/contexts/AudioContext';
import { useWelcome } from '@/contexts/WelcomeContext';
// // import { parseBlob } from 'music-metadata-browser'; // Temporarily disabled

export function Playlist({ 
  playlist, 
  currentIndex,
  isPlaying,
  onFileUpload, 
  onSongSelect, 
  onSongRemove, 
  onReorder,
  onPlay,
  onPause,
  isStreamerMode,
  onCollapse,
  isFloating = false,
  totalPlaylistDuration 
}) {
  const [reorderablePlaylist, setReorderablePlaylist] = useState(playlist);
  const [lyricsManagerOpen, setLyricsManagerOpen] = useState(false);
  const [selectedSongForLyrics, setSelectedSongForLyrics] = useState(null);
  const [lyricsStates, setLyricsStates] = useState({});
  const [musicFolderPath, setMusicFolderPath] = useState(null);
  const [isElectron, setIsElectron] = useState(false);
  const fileInputRef = useRef(null);
  const { resetWelcome } = useWelcome();
  
  const { normalizationApplied, showNormalizationIndicator } = useAudioContext();

  // Check if running in Electron and load saved music folder
  useEffect(() => {
    const checkElectron = () => {
      // First check if we're in an Electron build environment
      const isBuildElectron = import.meta.env.VITE_ELECTRON === 'true';
      
      // Runtime API check for additional verification
      const hasElectronAPI = typeof window !== 'undefined' && window.electronAPI !== undefined;
      
      // If we're in an Electron build OR have the API available, consider it Electron
      return isBuildElectron || hasElectronAPI;
    };
    
    // Add retry mechanism for API loading with more aggressive timeout
    let retryCount = 0;
    const maxRetries = 20; // Increased retries
    let timeoutId;
    
    const trySetElectron = () => {
      const electronAvailable = checkElectron();
      
      if (electronAvailable) {
        setIsElectron(true);
        // Load saved music folder path
        const savedPath = localStorage.getItem('sonicGroove_musicFolderPath');
        if (savedPath) {
          setMusicFolderPath(savedPath);
        }
      } else if (retryCount < maxRetries) {
        // Retry a few times in case API is still loading
        retryCount++;
        const delay = Math.min(50 * retryCount, 1000); // Cap at 1 second
        timeoutId = setTimeout(trySetElectron, delay);
      } else {
        // Not Electron environment
        setIsElectron(false);
      }
    };
    
    // Initial check with small delay to allow preload to finish
    const initialTimeoutId = setTimeout(trySetElectron, 100);
    
    return () => {
      clearTimeout(initialTimeoutId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Music folder selection
  const handleSelectMusicFolder = async () => {
    if (!isElectron) {
      toast({
        title: "Feature Not Available",
        description: "Music folder selection is only available in the desktop version.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Final runtime check before API call
      if (typeof window === 'undefined' || !window.electronAPI?.selectFolder) {
        toast({
          title: "Desktop Feature",
          description: "This feature requires the desktop version of Sonic Groove.",
          variant: "destructive"
        });
        return;
      }

      const result = await window.electronAPI.selectFolder();
      if (result && !result.canceled && result.filePaths.length > 0) {
        const folderPath = result.filePaths[0];
        
        // Check if folder has any music files
        try {
          const files = await window.electronAPI.scanMusicFolder(folderPath);
          if (!files || files.length === 0) {
            toast({
              title: "Empty Music Folder",
              description: "⚠️ The selected folder doesn't contain any music files. Make sure to copy your music files to this folder for playlist import to work properly.",
              variant: "destructive",
              duration: 8000, // Show longer since it's important
            });
          } else {
            toast({
              title: "Music Folder Set",
              description: `✅ Found ${files.length} music files in: ${folderPath}`,
            });
          }
        } catch (error) {
          // If scanning fails, still set the folder but warn
          toast({
            title: "Music Folder Set",
            description: `⚠️ Set music folder to: ${folderPath}\nNote: Unable to scan folder contents.`,
          });
        }
        
        setMusicFolderPath(folderPath);
        localStorage.setItem('sonicGroove_musicFolderPath', folderPath);
      }
    } catch (error) {
      console.error('Error selecting music folder:', error);
      toast({
        title: "Error",
        description: "Failed to select music folder. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Enhanced file scanning in music folder
  const scanMusicFolder = async (songMetadata) => {
    if (!isElectron || !musicFolderPath) return null;
    
    try {
      // Final runtime check before API call
      if (typeof window === 'undefined' || !window.electronAPI?.scanMusicFolder) {
        return null;
      }
      
      const files = await window.electronAPI.scanMusicFolder(musicFolderPath);
      
      // Try to match files by various criteria
      const matchingFileData = files.find(file => {
        const fileName = file.name;
        const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
        
        return (
          // Exact filename match
          songMetadata.name === fileName ||
          songMetadata.originalName === fileName ||
          // Title match
          songMetadata.title === fileName ||
          songMetadata.title === fileNameWithoutExt ||
          // Fuzzy matching for common variations
          fileName.toLowerCase().includes(songMetadata.title.toLowerCase()) ||
          (songMetadata.artist && fileName.toLowerCase().includes(songMetadata.artist.toLowerCase()))
        );
      });
      
      if (matchingFileData) {
        // Return the file data with path - don't create File object here
        return {
          path: matchingFileData.path,
          name: matchingFileData.name,
          size: matchingFileData.size
        };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // Enhanced import with folder scanning
  const handleImportPlaylist = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const importData = JSON.parse(e.target.result);
          
          if (!importData.songs || !Array.isArray(importData.songs)) {
            throw new Error('Invalid playlist format');
          }

          let restoredCount = 0;
          let placeholderCount = 0;

          if (isElectron && musicFolderPath) {
            // For Electron: Try to restore files from saved paths and folder scanning
            const restoredFiles = [];
            
            for (const song of importData.songs) {
              let fileRestored = false;
              
              // First try: Use saved file path if available
              if (song.filePath && window.electronAPI?.readFile) {
                try {
                  const fileBuffer = await window.electronAPI.readFile(song.filePath);
                  const fileName = song.filePath.split(/[\\/]/).pop();
                  const file = new File([fileBuffer], fileName, { type: 'audio/mpeg' });
                  restoredFiles.push(file);
                  restoredCount++;
                  fileRestored = true;
                } catch (error) {
                  // File not found at saved path, will try folder scan
                }
              }
              
              // Second try: Scan music folder for matching files
              if (!fileRestored) {
                const foundFile = await scanMusicFolder(song);
                if (foundFile && window.electronAPI?.readFile) {
                  try {
                    const fileBuffer = await window.electronAPI.readFile(foundFile.path);
                    const file = new File([fileBuffer], foundFile.name, { type: 'audio/mpeg' });
                    restoredFiles.push(file);
                    restoredCount++;
                    fileRestored = true;
                  } catch (error) {
                    console.error(`Error reading file: ${foundFile.path}`, error);
                  }
                }
              }
              
              if (!fileRestored) {
                placeholderCount++;
              }
            }
            
            // Clear current playlist and add restored files
            if (restoredFiles.length > 0) {
              // Clear current playlist first
              onReorder([]);
              
              // Add restored files using normal file upload process
              setTimeout(() => {
                onFileUpload(restoredFiles);
              }, 100);
            }
            
            // Create placeholders for unrestored songs
            if (placeholderCount > 0) {
              const placeholderSongs = importData.songs
                .filter(song => {
                  // Check if this song was restored
                  return !restoredFiles.some(file => 
                    file.name === song.name || 
                    file.name === song.originalName ||
                    file.name.includes(song.title)
                  );
                })
                .map(song => ({
                  id: song.id || `placeholder-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
                  name: song.name || 'Unknown',
                  title: song.title || song.name || 'Unknown Track',
                  artist: song.artist || 'Unknown Artist',
                  album: song.album || 'Unknown Album',
                  year: song.year || null,
                  duration: song.duration || 0,
                  size: song.size || 0,
                  type: song.type || 'audio/mpeg',
                  file: null,
                  albumArt: null,
                  url: null,
                  metadata: null,
                  isPlaceholder: true,
                  originalName: song.name || song.title || 'Unknown',
                  filePath: song.filePath || null
                }));
              
              // Add placeholders after restored files
              setTimeout(() => {
                onReorder(prev => [...prev, ...placeholderSongs]);
              }, 200);
            }
          } else {
            // For browser: Create placeholders for all songs
            const placeholderSongs = importData.songs.map(song => ({
              id: song.id || `placeholder-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
              name: song.name || 'Unknown',
              title: song.title || song.name || 'Unknown Track',
              artist: song.artist || 'Unknown Artist',
              album: song.album || 'Unknown Album',
              year: song.year || null,
              duration: song.duration || 0,
              size: song.size || 0,
              type: song.type || 'audio/mpeg',
              file: null,
              albumArt: null,
              url: null,
              metadata: null,
              isPlaceholder: true,
              originalName: song.name || song.title || 'Unknown',
              filePath: null
            }));
            
            onReorder(placeholderSongs);
            placeholderCount = placeholderSongs.length;
          }
          
          let message = `Imported ${importData.songs.length} songs.`;
          if (restoredCount > 0) {
            message += ` ${restoredCount} files automatically restored.`;
          }
          if (placeholderCount > 0) {
            message += ` ${placeholderCount} files need to be re-added.`;
          }
          
          toast({
            title: "Playlist Imported!",
            description: message,
          });
          
        } catch (error) {
          console.error('Import error:', error);
          toast({
            title: "Import Failed",
            description: "Invalid playlist file format. Please check the file and try again.",
            variant: "destructive"
          });
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  };

  const updateAllLyricsStates = useCallback(() => {
    const newLyricsStates = {};
    playlist.forEach(song => {
      const songId = getSongFilename(song) || song.id; 
      const songHasLyrics = hasLyrics(song);
      newLyricsStates[songId] = songHasLyrics;
    });
    setLyricsStates(newLyricsStates);
  }, [playlist]);

  useEffect(() => {
    setReorderablePlaylist(playlist);
    updateAllLyricsStates();
  }, [playlist, updateAllLyricsStates]);

  useEffect(() => {
    if (!lyricsManagerOpen) {
      updateAllLyricsStates();
    }
  }, [lyricsManagerOpen, updateAllLyricsStates]);

  useEffect(() => {
    const handleLyricsDataChange = (event) => {
      updateAllLyricsStates();
    };
    
    const handleStorageChange = (event) => {
      if (event.key === 'sonicGrooveLyrics' || event.key === null) { 
        updateAllLyricsStates();
      }
    };

    window.addEventListener(LYRICS_CHANGED_EVENT, handleLyricsDataChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(LYRICS_CHANGED_EVENT, handleLyricsDataChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [updateAllLyricsStates]);

  // Enhanced file upload with smart matching for placeholders
  const handleFileInputChangeWithMatching = async (e) => {
    const files = e.target.files;
    
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      
      const hasPlaceholders = playlist.some(song => song.isPlaceholder);
      
      if (hasPlaceholders) {
        // Try to match files to placeholders
        const matchedFiles = new Set();
        const updatedPlaylist = playlist.map(song => {
          if (!song.isPlaceholder) return song;
          
          // Find matching file by name
          const matchingFile = filesArray.find(file => {
            const fileName = file.name;
            const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
            return (
              !matchedFiles.has(file) && (
                song.originalName === fileName ||
                song.name === fileName ||
                song.title === fileName ||
                song.title === fileNameWithoutExt ||
                song.name === fileNameWithoutExt
              )
            );
          });
          
          if (matchingFile) {
            matchedFiles.add(matchingFile);
            return {
              ...song,
              file: matchingFile,
              isPlaceholder: false,
              size: matchingFile.size,
              type: matchingFile.type,
              filePath: matchingFile.path || null // Store file path if available
            };
          }
          
          return song;
        });
        
        // Get unmatched files
        const unmatchedFiles = filesArray.filter(file => !matchedFiles.has(file));
        
        // Update playlist with matched files
        onReorder(updatedPlaylist);
        
        // Add unmatched files as new songs
        if (unmatchedFiles.length > 0) {
          onFileUpload(unmatchedFiles);
        }
        
        const matchedCount = matchedFiles.size;
        const unmatchedCount = unmatchedFiles.length;
        
        if (matchedCount > 0) {
          toast({
            title: "Files Matched!",
            description: `${matchedCount} songs restored${unmatchedCount > 0 ? `, ${unmatchedCount} new songs added` : ''}`,
          });
        } else if (unmatchedCount > 0) {
          toast({
            title: "Files Added",
            description: `${unmatchedCount} new songs added to playlist`,
          });
        }
      } else {
        // No placeholders, just add files normally
        onFileUpload(files);
      }
      
      e.target.value = '';
    }
  };

  const handleFileInputChange = handleFileInputChangeWithMatching;

  const handleUploadClick = async () => {
    if (isElectron) {
      // In Electron, use the dialog API to get file paths
      try {
        // Final runtime check before API call
        if (typeof window === 'undefined' || !window.electronAPI?.selectMusicFiles) {
          toast({
            title: "Desktop Feature",
            description: "This feature requires the desktop version of Sonic Groove.",
            variant: "destructive"
          });
          return;
        }
        
        const result = await window.electronAPI.selectMusicFiles();
        if (result && !result.canceled && result.filePaths.length > 0) {
          await addSongsFromPaths(result.filePaths);
        }
      } catch (error) {
        console.error('Error selecting music files:', error);
        toast({
          title: "Error",
          description: "Failed to select music files. Please try again.",
          variant: "destructive"
        });
      }
    } else {
      // In browser, use file input
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleSongClick = async (index, event) => {
    if (event.target.closest('.drag-handle') || 
        event.target.closest('.remove-button') || 
        event.target.closest('.lyrics-button')) {
      return;
    }

    const song = playlist[index];
    
    if (!song.file) {
      toast({
        title: "File Not Available",
        description: "This song file is no longer available. Please re-add the file to your playlist.",
        variant: "destructive"
      });
      return;
    }

    if (index === currentIndex) {
      // Same song - toggle play/pause
      if (isPlaying) {
        onPause();
      } else {
        await onPlay();
      }
    } else {
      // Different song - always play it if we were playing before
      const wasPlaying = isPlaying;
      
      // Select the new song first
      onSongSelect(index);
      
      // Always try to play if we were previously playing, or if user explicitly clicked
      if (wasPlaying) {
        // Use requestAnimationFrame to ensure the song selection has been processed
        requestAnimationFrame(async () => {
          try {
            await onPlay();
          } catch (error) {
            console.error('Error auto-playing new song:', error);
          }
        });
      }
    }
  };

  const handleReorder = (newOrder) => {
    setReorderablePlaylist(newOrder);
    onReorder(newOrder);
  };

  const handleAddLyrics = (song) => {
    setSelectedSongForLyrics(song);
    setLyricsManagerOpen(true);
  };

  const handleLyricsChange = (song, lyricsData) => {
    const songId = getSongFilename(song) || song.id;
    setLyricsStates(prev => ({
      ...prev,
      [songId]: !!lyricsData 
    }));
    // No need to call updateAllLyricsStates here, custom event will handle it
  };

  const handleLyricsManagerClose = () => {
    setLyricsManagerOpen(false);
    setSelectedSongForLyrics(null);
    // updateAllLyricsStates will be triggered by the lyricsManagerOpen useEffect
  };

  // Enhanced export/import functionality with folder support
  const handleExportPlaylist = () => {
    if (playlist.length === 0) {
      toast({
        title: "Empty Playlist",
        description: "Add some songs to your playlist before exporting.",
        variant: "destructive"
      });
      return;
    }

    const exportData = {
      name: `Sonic Groove Playlist - ${new Date().toLocaleDateString()}`,
      version: "1.1", // Updated version to indicate folder support
      exported: new Date().toISOString(),
      musicFolderPath: musicFolderPath, // Include current music folder path
      songs: playlist.map(song => ({
        id: song.id,
        name: song.name,
        title: song.title,
        artist: song.artist,
        album: song.album,
        year: song.year,
        duration: song.duration,
        size: song.size,
        type: song.type,
        filePath: song.filePath || null, // Include file path for Electron persistence
        originalName: song.originalName || song.name
      }))
    };
    
    // Debug logging for export
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `sonic-groove-playlist-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Playlist Exported!",
      description: "Your playlist has been downloaded as a JSON file.",
    });
  };

  // Convert file path to File object for Electron
  const createFileFromPath = async (filePath) => {
    if (!isElectron || !filePath) return null;
    
    try {
      if (typeof window !== 'undefined' && window.electronAPI?.readFile) {
        const fileData = await window.electronAPI.readFile(filePath);
        // Extract filename from path using basic string manipulation (works cross-platform)
        const fileName = filePath.split(/[\\/]/).pop();
        return new File([fileData], fileName, { type: 'audio/mpeg' });
      }
      return null;
    } catch (error) {
      console.error('Error creating file from path:', error);
      return null;
    }
  };

  // Enhanced playlist management for Electron with file path support
  const addSongsFromPaths = async (filePaths) => {
    if (!isElectron || !filePaths || filePaths.length === 0) return;
    
    const newFiles = [];
    
    for (const filePath of filePaths) {
      try {
        // Check if electronAPI is available at runtime
        if (typeof window === 'undefined' || !window.electronAPI?.readFile) {
          console.warn('electronAPI.readFile not available');
          continue;
        }
        
        // Create File object from path
        const fileBuffer = await window.electronAPI.readFile(filePath);
        const fileName = filePath.split(/[\\/]/).pop();
        const file = new File([fileBuffer], fileName, { type: 'audio/mpeg' });
        
        // Add the file path as a property for persistence
        Object.defineProperty(file, 'path', {
          value: filePath,
          writable: false,
          enumerable: true, // Make it enumerable so it transfers properly
          configurable: false
        });
        
        newFiles.push(file);
      } catch (error) {
        console.error(`Error processing file ${filePath}:`, error);
        toast({
          title: "Error Processing File",
          description: `Could not process: ${filePath.split(/[\\/]/).pop()}`,
          variant: "destructive"
        });
      }
    }
    
    if (newFiles.length > 0) {
      // Use the normal file upload process which will call addSongs
      onFileUpload(newFiles);
    }
  };

  // Function to restore songs from file paths (for app restart)
  const restoreSongsFromPaths = async (songsWithPaths) => {
    if (!isElectron) return songsWithPaths;
    
    const restoredSongs = await Promise.all(
      songsWithPaths.map(async (song) => {
        if (song.filePath && !song.file && window.electronAPI?.readFile) {
          try {
            const fileBuffer = await window.electronAPI.readFile(song.filePath);
            const fileName = song.filePath.split(/[\\/]/).pop();
            const file = new File([fileBuffer], fileName, { type: 'audio/mpeg' });
            
            return {
              ...song,
              file: file,
              isPlaceholder: false
            };
          } catch (error) {
            console.error(`Error restoring file ${song.filePath}:`, error);
            return {
              ...song,
              file: null,
              isPlaceholder: true
            };
          }
        }
        return song;
      })
    );
    
    return restoredSongs;
  };

  // Auto-restore songs with file paths on component mount
  useEffect(() => {
    if (isElectron && playlist.length > 0) {
      const songsWithPaths = playlist.filter(song => song.filePath && !song.file);
      if (songsWithPaths.length > 0) {
        restoreSongsFromPaths(playlist).then(restoredPlaylist => {
          if (restoredPlaylist !== playlist) {
            onReorder(restoredPlaylist);
          }
        });
      }
    }
  }, [isElectron, playlist, onReorder]);

  const containerPadding = isFloating ? 'p-3' : 'p-4';
  const headerMargin = isFloating ? 'mb-3' : 'mb-4';

  return (
    <div className={`h-full flex flex-col ${containerPadding}`}>
      {!isFloating && (
        <div className={`flex items-center justify-between ${headerMargin}`}>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Music className="h-5 w-5" />
            Playlist ({playlist.length})
          </h2>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onCollapse}
            className="text-white/60 hover:text-white hover:bg-white/10"
            title="Collapse Playlist"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Button
        onClick={handleUploadClick}
        className={`w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white ${isFloating ? 'mb-2' : 'mb-3'}`}
      >
        <Upload className="h-4 w-4 mr-2" />
        Add Songs
      </Button>
      
      {/* Export/Import/Music Folder buttons - show in Electron builds */}
      {!isFloating && isElectron && (
        <div className="flex gap-1 mb-3">
          <Button
            onClick={handleExportPlaylist}
            disabled={playlist.length === 0}
            size="sm"
            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white disabled:opacity-50"
          >
            <Download className="h-3 w-3 mr-1" />
            Export
          </Button>
          
          <Button
            onClick={handleImportPlaylist}
            size="sm"
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
          >
            <FolderOpen className="h-3 w-3 mr-1" />
            Import
          </Button>
          
          {/* Only show Folder button if API is available */}
          {typeof window !== 'undefined' && window.electronAPI && (
            <Button
              onClick={handleSelectMusicFolder}
              size="sm"
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              title={musicFolderPath ? `Current: ${musicFolderPath}` : 'Set music folder for better file restoration'}
            >
              <Folder className="h-3 w-3 mr-1" />
              Folder
            </Button>
          )}
        </div>
      )}
      
      {/* Music folder path display - compact */}
      {!isFloating && isElectron && musicFolderPath && (
        <div className="mb-2 p-2 bg-white/5 rounded border border-white/10">
          <p className="text-xs text-white/60">Music Folder:</p>
          <p className="text-xs text-white/80 truncate" title={musicFolderPath}>
            {musicFolderPath}
          </p>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac,.ost"
        onChange={handleFileInputChange}
        className="hidden"
      />

      <div className="flex-1 overflow-hidden mt-3">
        {playlist.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-white/60">
            <FileAudio className="h-12 w-12 mb-3" />
            <p className="text-center">No songs in playlist</p>
            <p className="text-sm text-center mt-1">Click "Add Songs" to get started</p>
            <div className="mt-3 flex flex-col items-center gap-1 text-xs text-center">
              <a
                href="https://ko-fi.com/bliponskrine"
                target="_blank"
                rel="noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                More info and full app here!
              </a>
              <button
                type="button"
                onClick={resetWelcome}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Relaunch the Welcome Page Popup
              </button>
            </div>
          </div>
        ) : (
          <div className={`overflow-y-auto h-full ${isFloating ? 'pr-1' : 'pr-2'}`}>
            <Reorder.Group 
              axis="y" 
              values={reorderablePlaylist} 
              onReorder={handleReorder}
              as="div"
              className="space-y-2 playlist-container"
            >
              {reorderablePlaylist.map((song, index) => {
                const songId = getSongFilename(song) || song.id;
                const isPlaceholder = song.isPlaceholder;
                
                if (isPlaceholder) {
                  // Simple placeholder item
                  return (
                    <Reorder.Item
                      key={song.id}
                      value={song}
                      id={song.id}
                      className="group playlist-item"
                    >
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-colors">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-amber-400/60" />
                          <FileAudio className="h-4 w-4 text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-amber-400 truncate">
                            {song.title || song.name || 'Unknown Track'}
                          </h4>
                          <p className="text-xs text-amber-400/70 mt-1">
                            📁 File needed - {song.artist || 'Unknown Artist'}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSongRemove(index);
                          }}
                          className="h-6 w-6 p-0 text-amber-400/60 hover:text-red-400 hover:bg-red-500/20"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </Reorder.Item>
                  );
                }
                
                return (
                  <PlaylistItem
                    key={song.id}
                    song={song}
                    index={index}
                    currentIndex={currentIndex}
                    isPlaying={isPlaying}
                    hasLyrics={lyricsStates[songId]}
                    onSongClick={handleSongClick}
                    onSongRemove={onSongRemove}
                    onAddLyrics={handleAddLyrics}
                    isCompact={isFloating}
                  />
                );
              })}
            </Reorder.Group>
          </div>
        )}
      </div>

      {!isFloating && (
        <PlaylistStats
          playlist={playlist}
          currentIndex={currentIndex}
          isPlaying={isPlaying}
          totalDuration={totalPlaylistDuration} 
          normalizationApplied={normalizationApplied}
          showNormalizationIndicator={showNormalizationIndicator}
        />
      )}

      <LyricsManager
        song={selectedSongForLyrics}
        isOpen={lyricsManagerOpen}
        onClose={handleLyricsManagerClose}
        onLyricsLoaded={(lyricsData) => {
          if (selectedSongForLyrics) {
            handleLyricsChange(selectedSongForLyrics, lyricsData);
            // updateAllLyricsStates(); // Custom event will trigger this
          }
        }}
        onLyricsCleared={() => {
          if (selectedSongForLyrics) {
            handleLyricsChange(selectedSongForLyrics, null);
            // updateAllLyricsStates(); // Custom event will trigger this
          }
        }}
      />
    </div>
  );
}